import * as React from "react";

export class Button extends React.PureComponent<Button.Props> {
  private buttonClasses = "text-center px-1-6 font-bold text-xs";
  private colours = {
    primary: {
      default: `bg-black text-white pt-1-1 pb-1 tracking-wide border border-black ${
        this.buttonClasses
      } hover:bg-white hover:text-black`,
      disabled: `bg-black text-white opacity-75 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    "primary-reverse": {
      default: `border border-white bg-white text-black hover:bg-blue-60 hover:text-white ${
        this.buttonClasses
      }`,
      disabled: `border border-white bg-white text-black opacity-30 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    underline: {
      default:
        "mt-1-3 md:mt-1-8 flex items-center underline cursor-pointer uppercase",
      disabled:
        "mt-1-3 md:mt-1-8 flex items-center underline cursor-pointer uppercase"
    },
    secondary: {
      default: `border-half border-black pt-1-1 pb-1 md:transition text-black hover:text-white bg-white hover:bg-black ${
        this.buttonClasses
      }`,
      disabled: `border-half border-black text-black bg-white opacity-30 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    "secondary-reverse": {
      default: `border-half border-white pt-1-1 pb-1 transition text-white hover:text-black bg-white hover:bg-white ${
        this.buttonClasses
      }`,
      disabled: `border border-white text-white opacity-30 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    "secondary-transparent": {
      default: `border-half border-black pt-1-1 pb-1 transition text-black hover:text-white bg-transparent hover:bg-black ${
        this.buttonClasses
      }`,
      disabled: `border-half border-black text-black bg-transparent opacity-30 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    "secondary-transparent-reverse": {
      default: `border-half border-white pt-1-1 pb-1 transition text-white hover:text-black hover:border-black bg-transparent hover:bg-white ${
        this.buttonClasses
      }`,
      disabled: `border-half border-white text-white bg-transparent opacity-30 cursor-not-allowed ${
        this.buttonClasses
      }`
    },
    link: {
      default: "font-bold text-black tracking-wide uppercase text-sm",
      disabled: "font-bold text-black tracking-wide uppercase text-sm"
    },
    "link-large": {
      default: "font-bold text-black tracking-wide uppercase text-base",
      disabled: "font-bold text-black tracking-wide uppercase text-base"
    },
    "link-small": {
      default: "font-bold text-black tracking-wide uppercase text-xs",
      disabled: "font-bold text-black tracking-wide uppercase text-xs"
    },
    input: {
      default:
        "border-blue-40 border rounded bg-white font-bold text-black text-left px-1-4",
      disabled:
        "border-blue-40 border rounded bg-white font-bold text-black text-left px-1-4"
    },
    icon: {
      default:
        "rounded-full h-4 w-4 flex items-center justify-center focus:outline-none cursor-pointer",
      disabled:
        "rounded-full h-4 w-4 flex items-center justify-center focus:outline-none cursor-not-allowed"
    },
    blank: {
      default: "",
      disabled: ""
    }
  };

  public render(): JSX.Element {
    const {
      href,
      className,
      id,
      disabled,
      type,
      onClick,
      onMouseLeave,
      buttonStyle = "border",
      onMouseEnter,
      target,
      children,
      title,
      refs
    } = this.props;
    const colour = this.props.colour || "primary";
    const colourClass = disabled
      ? this.colours[colour].disabled
      : this.colours[colour].default;

    const classes = `${colourClass} ${className ? className : ""} ${
      colour.includes("link") ||
      colour.includes("blank") ||
      colour.includes("underline")
        ? ""
        : `no-underline ${!this.props.thin ? " " : "py-1"} md:transition-fast`
    }`;
    let classesCopy: String = classes;

    // Change the button styling to underline
    if (buttonStyle === "underline") {
      classesCopy = classes
        .split(" ")
        .filter(
          className =>
            !className.includes("border") &&
            !className.includes("hover:bg-") &&
            className !== "no-underline"
        )
        .join(" ");

      const bgHoverMap = {
        "bg-black": "hover:text-black",
        "bg-white": "hover:text-white"
      };

      Object.keys(bgHoverMap).forEach(bgClass => {
        if (classesCopy.includes(bgClass)) {
          const hoverClass = bgHoverMap[bgClass];
          classesCopy = classesCopy
            .split(" ")
            .filter(className => className !== hoverClass)
            .join(" ");
          classesCopy += " pb-1-1"; // offset for text underline
        }
      });

      classesCopy += " underline underline-offset-4";
    }

    const classesCopyTwo =
      classesCopy.includes("bg-transparent") && classesCopy.includes("bg-white")
        ? classesCopy.replace("bg-white", "")
        : classesCopy;

    return href ? (
      <a
        id={id}
        href={href}
        className={`${classesCopyTwo} inline-block`}
        onClick={onClick ? onClick : null}
        target={href.includes(".pdf") ? "_blank" : target ? target : null}
        title={title}
        ref={refs}
        rel="noreferrer"
      >
        {children}
      </a>
    ) : (
      <button
        id={id}
        type={type ? type : "button"}
        role={type === "button" ? "button" : null}
        // @ts-ignore
        name={`${name ? name : ""}`}
        disabled={disabled ? disabled : null}
        className={`${classesCopyTwo}`}
        onClick={onClick ? onClick : null}
        onMouseEnter={onMouseEnter ? onMouseEnter : null}
        onMouseLeave={onMouseLeave ? onMouseLeave : null}
        aria-label={title}
        ref={refs}
      >
        {children}
      </button>
    );
  }
}

export namespace Button {
  export interface Props {
    id?: string;
    className?: string;
    colour?: string;
    buttonStyle?: string;
    target?: string;
    onClick?: any;
    onMouseEnter?: any;
    onMouseLeave?: any;
    href?: string;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    name?: string;
    title: string;
    thin?: boolean;
    refs?: string;
  }
}
